import { skipToken, useQuery } from "@tanstack/react-query";
import { useAui, useAuiState } from "@assistant-ui/react";
import { RotateCcwIcon } from "lucide-react";
import type { FC } from "react";

import type { IChatRecoveryStatus } from "@/api/chat/chat.types";
import { TooltipIconButton } from "@/components/assistant-ui/tooltip-icon-button";
import {
    isRecoveryTerminal,
    recoveryStatusQueryKey,
    shouldExposeRetry,
} from "@/hooks/chat/useMessageRecoveryStatus";

import { useRetryTarget } from "../contexts/RetryContext";
import { startRecoveryRetry } from "../utils/startRecoveryRetry";

/**
 * Action-bar retry for a failed turn the server says can be re-run.
 *
 * Renders in the assistant action bar so retry sits where every other
 * message action lives. Subscribes to the recovery status the message's
 * RecoveringMessageSlot already fetched -- `skipToken` makes this a cache
 * read, never a poll -- so the button appears exactly when a terminal row
 * carries the server's retry authorisation. Stalled-but-active turns keep
 * their inline affordance instead: their `running` status hides the bar.
 */
export const RecoveryRetryButton: FC = () => {
    const aui = useAui();
    const conversationId = useAuiState(
        (s) => s.message.metadata?.custom?.conversation_id as number | undefined
    );
    const messageId = useAuiState((s) => Number(s.message.id));
    const parentId = useAuiState((s) => s.message.parentId);
    const retryTarget = useRetryTarget();

    const { data } = useQuery<IChatRecoveryStatus | undefined>({
        queryKey: recoveryStatusQueryKey(conversationId ?? 0, messageId),
        queryFn: skipToken,
    });

    if (retryTarget === undefined || parentId === null) return null;
    if (data === undefined) return null;
    if (!isRecoveryTerminal(data) || !shouldExposeRetry(data)) return null;

    return (
        <TooltipIconButton
            tooltip="Try again"
            onClick={() =>
                startRecoveryRetry(aui, retryTarget, messageId, parentId)
            }
        >
            <RotateCcwIcon />
        </TooltipIconButton>
    );
};
